import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AlertCircle, Check } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';
import { videoApiClient } from '@/services/videoApi';

const AUTOSAVE_DELAY_MS = 1200;

// Saves still in flight, across every editor on the page. Listing fetches and
// the CSV export wait on these so they never read a note mid-save.
const pendingSaves = new Set();

export const waitForPendingFeedbackSaves = () => Promise.allSettled([...pendingSaves]);

const relativeTime = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });

const formatSavedAt = (dateStr) => {
  const seconds = Math.round((new Date(dateStr).getTime() - Date.now()) / 1000);
  if (Math.abs(seconds) < 60) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (Math.abs(minutes) < 60) return relativeTime.format(minutes, 'minute');
  const hours = Math.round(minutes / 60);
  if (Math.abs(hours) < 24) return relativeTime.format(hours, 'hour');
  const days = Math.round(hours / 24);
  if (Math.abs(days) < 7) return relativeTime.format(days, 'day');
  return new Date(dateStr).toLocaleDateString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric'
  });
};

/**
 * The team's free-text review note on a video. There is one note per video,
 * shared by everyone (last save wins). It autosaves once typing pauses, on
 * blur and on Cmd/Ctrl+Enter; pending text is flushed on unmount.
 */
export function VideoFeedbackEditor({
  jobId,
  note,
  onSaved,
  readOnly = false,
  className,
  textareaClassName,
}) {
  const savedText = note?.feedback ?? '';
  const [text, setText] = useState(savedText);
  const [status, setStatus] = useState('idle'); // idle | dirty | saving | error
  const textRef = useRef(savedText);
  const serverTextRef = useRef(savedText);
  const inFlightRef = useRef(false);
  const saveAgainRef = useRef(false);
  const timerRef = useRef(null);
  const focusedRef = useRef(false);
  const onSavedRef = useRef(onSaved);

  useEffect(() => {
    onSavedRef.current = onSaved;
  }, [onSaved]);

  const save = useCallback(async () => {
    clearTimeout(timerRef.current);
    if (inFlightRef.current) {
      saveAgainRef.current = true;
      return;
    }
    if (textRef.current.trim() === serverTextRef.current.trim()) {
      setStatus('idle');
      return;
    }

    inFlightRef.current = true;
    setStatus('saving');
    const request = videoApiClient.saveVideoFeedback(jobId, textRef.current);
    pendingSaves.add(request);
    try {
      const saved = await request;
      serverTextRef.current = saved?.feedback ?? '';
      onSavedRef.current?.(jobId, saved);
      setStatus(textRef.current.trim() === serverTextRef.current.trim() ? 'idle' : 'dirty');
    } catch (err) {
      console.error('Failed to save video feedback:', err);
      setStatus('error');
    } finally {
      pendingSaves.delete(request);
      inFlightRef.current = false;
      if (saveAgainRef.current) {
        saveAgainRef.current = false;
        save();
      }
    }
  }, [jobId]);

  // Adopt a note saved elsewhere (e.g. the player dialog) unless this editor
  // is being typed in or holds edits of its own.
  useEffect(() => {
    const hasOwnEdits = textRef.current.trim() !== serverTextRef.current.trim();
    serverTextRef.current = savedText;
    if (!hasOwnEdits && !focusedRef.current) {
      textRef.current = savedText;
      setText(savedText);
      setStatus('idle');
    }
  }, [savedText]);

  // Flush unsaved text when the editor goes away (page switch, dialog close).
  useEffect(() => () => {
    clearTimeout(timerRef.current);
    if (!inFlightRef.current && textRef.current.trim() !== serverTextRef.current.trim()) {
      save();
    }
  }, [save]);

  useEffect(() => {
    if (status === 'idle') return undefined;
    const warnBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warnBeforeUnload);
    return () => window.removeEventListener('beforeunload', warnBeforeUnload);
  }, [status]);

  const handleChange = (event) => {
    textRef.current = event.target.value;
    setText(event.target.value);
    setStatus('dirty');
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(save, AUTOSAVE_DELAY_MS);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      save();
    }
  };

  const author = note?.updated_by_name || note?.updated_by_email;

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <Textarea
        value={text}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={() => { focusedRef.current = true; }}
        onBlur={() => { focusedRef.current = false; save(); }}
        readOnly={readOnly}
        placeholder={readOnly ? 'No feedback yet.' : 'What did you think of this video?'}
        aria-label="Video feedback"
        className={cn('min-h-20 max-h-48 resize-none text-sm', textareaClassName)}
      />
      <p className="flex min-h-4 items-center gap-1.5 text-xs text-muted-foreground" aria-live="polite">
        {status === 'saving' && (
          <>
            <Spinner size="sm" className="size-3 text-muted-foreground" />
            Saving…
          </>
        )}
        {status === 'dirty' && 'Unsaved changes'}
        {status === 'error' && (
          <span className="flex items-center gap-1 text-destructive">
            <AlertCircle className="size-3" />
            Couldn't save.
            <button type="button" onClick={save} className="font-medium underline underline-offset-2">
              Retry
            </button>
          </span>
        )}
        {status === 'idle' && note && (
          <>
            <Check className="size-3 text-emerald-600" />
            Saved{author ? ` by ${author}` : ''} · {formatSavedAt(note.updated_at)}
          </>
        )}
        {status === 'idle' && !note && !readOnly && 'Saves automatically as you type'}
      </p>
    </div>
  );
}
